import { useEffect, useRef, useState, useCallback } from 'react';

// Sequence: Haut, Haut, Bas, Bas, Gauche, Droite
export type Direction = 'up' | 'down' | 'left' | 'right';
const KONAMI_SEQUENCE: Direction[] = ['up', 'up', 'down', 'down', 'left', 'right'];

interface UseKonamiCodeOptions {
  onSuccess: () => void;
}

export function useKonamiCode({ onSuccess }: UseKonamiCodeOptions) {
  const [progress, setProgress] = useState<Direction[]>([]);
  const timeoutRef = useRef<number | null>(null);

  const resetProgress = useCallback(() => {
    setProgress([]);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const pushDirection = useCallback(
    (dir: Direction) => {
      setProgress((prev) => {
        const next = [...prev, dir];
        // Check if the input so far matches the prefix of KONAMI_SEQUENCE
        const isMatchSoFar = next.every((val, idx) => val === KONAMI_SEQUENCE[idx]);

        if (!isMatchSoFar) {
          // If mismatch, check if the single key pressed can start a new sequence ('up')
          return dir === 'up' ? ['up'] : [];
        }

        if (next.length === KONAMI_SEQUENCE.length) {
          // Success!
          onSuccess();
          return [];
        }

        // Auto-reset if user pauses for more than 4 seconds
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(resetProgress, 4000);

        return next;
      });
    },
    [onSuccess, resetProgress]
  );

  // Keyboard listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is currently typing inside an input or textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      let dir: Direction | null = null;
      if (e.key === 'ArrowUp' || e.key === 'z' || e.key === 'Z' || e.key === 'w' || e.key === 'W') {
        dir = 'up';
      } else if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') {
        dir = 'down';
      } else if (e.key === 'ArrowLeft' || e.key === 'q' || e.key === 'Q' || e.key === 'a' || e.key === 'A') {
        dir = 'left';
      } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        dir = 'right';
      }

      if (dir) {
        pushDirection(dir);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [pushDirection]);

  return {
    progress,
    pushDirection,
    resetProgress,
  };
}

